import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { WorkOrderModel } from '@/models/WorkOrderModel';

class InvoicePdfService
{
    public generateInvoicePDF(workOrder: WorkOrderModel): void
    {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 15;
        
        let yPos = 15;
        
        // ROW 1: Company Logo (left) and INVOICE title (right)
        // Add logo image - adjust path, width, and height as needed
        const logoPath = '/images/7LT-Logo-Hang-8-400x131.png'; // Update this path to your logo file
        const logoWidth = 68; // Adjust logo width
        const logoHeight = 21; // Adjust logo height
        try {
            doc.addImage(logoPath, 'PNG', margin, yPos - 5, logoWidth, logoHeight);
        } catch (error) {
            console.warn('Logo image failed to load:', error);
            // Fallback to text if image fails
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('7th Level Technologies', margin, yPos);
        }
        
        yPos = 20;
        
        doc.setFontSize(36);
        doc.setTextColor(180, 180, 180);
        const invoiceTitle = workOrder.Title || 'INVOICE';
        doc.text(invoiceTitle.toUpperCase(), pageWidth - margin, yPos, { align: 'right' });
        
        yPos += 15;
        
        // ROW 2: Business address and contact info
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text('482 State Route 93, Sugarloaf, PA 18249', margin, yPos);
        yPos += 5;
        doc.text('570.359.3411 | billing@7thleveltech.com | www.7thleveltech.com', margin, yPos);
        
        yPos += 10;
        
        // ROW 3: "Sold To" box (left) and Invoice details box (right)
        const boxStartY = yPos;
        const soldToBoxWidth = (pageWidth - 2 * margin) / 2 - 5;
        const invoiceBoxX = margin + soldToBoxWidth + 10;
        const invoiceBoxWidth = (pageWidth - 2 * margin) / 2 - 5;
        
        // Sold To box
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.rect(margin, boxStartY, soldToBoxWidth, 30);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('SOLD TO', margin + 2, boxStartY + 5);
        
        doc.setFont('helvetica', 'normal');
        let soldToY = boxStartY + 10;
        if (workOrder.Contact)
        {
            if (workOrder.Contact.Company)
            {
                doc.text(workOrder.Contact.Company, margin + 2, soldToY);
                soldToY += 5;
            }
            if (workOrder.Contact.ContactInformation)
            {
                const contactLines = doc.splitTextToSize(workOrder.Contact.ContactInformation, soldToBoxWidth - 4);
                doc.text(contactLines, margin + 2, soldToY);
            }
        }
        
        // Invoice details box (right side)
        const invoiceDetailsData = [
            ['INVOICE DATE', workOrder.DateCreated || 'N/A'],
            ['INVOICE #', workOrder.InvoiceNumber || 'N/A'],
            ['P.O. #', workOrder.PONumber || 'N/A'],
            ['ESTIMATE #', workOrder.EstimateNumber || 'N/A']
        ];
        
        autoTable(doc, {
            startY: boxStartY,
            body: invoiceDetailsData,
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: 2
            },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: invoiceBoxWidth * 0.5 },
                1: { cellWidth: invoiceBoxWidth * 0.5 }
            },
            margin: { left: invoiceBoxX },
            tableWidth: invoiceBoxWidth
        });
        
        yPos = Math.max(boxStartY + 35, (doc as any).lastAutoTable?.finalY + 5 || boxStartY + 35);
        
        // ROW 4: Big table - First header row (REQUESTOR, PROJECT/JOB ID, TERMS, DUE DATE)
        const requestorData = [
            [
                workOrder.Contact?.Company || 'N/A',
                workOrder.RequestId?.toString() || 'N/A',
                workOrder.Terms || 'Net 30',
                workOrder.DateDue || 'N/A'
            ]
        ];
        
        autoTable(doc, {
            startY: yPos,
            head: [['REQUESTOR', 'PROJECT / JOB ID', 'TERMS', 'DUE DATE']],
            body: requestorData,
            theme: 'grid',
            headStyles: {
                fillColor: [220, 220, 220],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 9
            },
            styles: {
                fontSize: 9,
                cellPadding: 3
            },
            columnStyles: {
                0: { cellWidth: 45 },
                1: { cellWidth: 40 },
                2: { cellWidth: 45 },
                3: { 
                    cellWidth: 'auto'
                }
            },
            didParseCell: function(data) {
                if (data.section === 'head' && data.column.index === 3) {
                    data.cell.styles.fillColor = [139, 0, 0];
                    data.cell.styles.textColor = [255, 255, 255];
                }
            },
            margin: { left: margin, right: margin }
        });
        
        yPos = (doc as any).lastAutoTable?.finalY || yPos + 20;
        
        // Line items section (SERVICE DATE, DESCRIPTION, TOTAL)
        const lineItemsData: any[] = [];
        if (workOrder.Items && workOrder.Items.length > 0)
        {
            workOrder.Items.forEach((item, index) => {
                lineItemsData.push([
                    item.LineDate || 'N/A',
                    item.Description || '',
                    '$' + (item.LineTotal || item.CalculatedLineTotal || 0).toFixed(2)
                ]);
                
                if (index < workOrder.Items!.length - 1)
                {
                    lineItemsData.push(['', '', '']);
                }
            });
        }
        
        autoTable(doc, {
            startY: yPos,
            head: [['SERVICE DATE', 'DESCRIPTION', 'TOTAL']],
            body: lineItemsData.length > 0 ? lineItemsData : [['N/A', 'No items', '$0.00']],
            theme: 'grid',
            headStyles: {
                fillColor: [220, 220, 220],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 9,
                valign: 'top'
            },
            styles: {
                fontSize: 9,
                cellPadding: 3,
                valign: 'top'
            },
            columnStyles: {
                0: { cellWidth: 30 },
                1: { cellWidth: 115 },
                2: { cellWidth: 'auto', halign: 'right' }
            },
            didParseCell: function(data) {
                // Check if this is a padding row (empty row between items)
                if (data.section === 'body' && data.row.index % 2 === 1 && lineItemsData.length > 1) {
                    data.cell.styles.fillColor = [245, 245, 245];
                    data.cell.styles.minCellHeight = 1;
                    data.cell.styles.cellPadding = 0.5;
                }
            },
            margin: { left: margin, right: margin }
        });
        
        yPos = (doc as any).lastAutoTable?.finalY + 5 || yPos + 20;
        
        // ROW 5 & 6: Terms/Conditions section with financial summary
        const termsText = workOrder.SpecialInstructions || workOrder.Terms || 'Standard terms and conditions apply.';
        const subtotal = workOrder.Subtotal || workOrder.CalculatedSubtotal || 0;
        const salesTax = workOrder.Tax || 0;
        const total = workOrder.Total || workOrder.CalculatedTotal || 0;
        const paymentsCredits = (workOrder.AmountPaid || 0) + (workOrder.Credit || 0);
        const balanceDue = total - paymentsCredits;
        
        const termsBoxWidth = (pageWidth - 2 * margin) * 0.6;
        const financialBoxWidth = (pageWidth - 2 * margin) * 0.4;
        const financialBoxX = margin + termsBoxWidth;
        
        // Header row
        autoTable(doc, {
            startY: yPos,
            body: [
                ['TERMS, CONDITIONS, AND OTHER INFORMATION', 'SUBTOTAL']
            ],
            theme: 'grid',
            styles: {
                fontSize: 9,
                fontStyle: 'bold',
                cellPadding: 2,
                fillColor: [220, 220, 220],
                textColor: [0, 0, 0]
            },
            columnStyles: {
                0: { cellWidth: termsBoxWidth },
                1: { cellWidth: financialBoxWidth, halign: 'left' }
            },
            margin: { left: margin, right: margin }
        });
        
        const termsHeaderY = (doc as any).lastAutoTable?.finalY || yPos + 7;
        
        // Terms box with financial details
        const financialData = [
            ['', '$' + subtotal.toFixed(2)],
            ['SALES TAX', '$' + salesTax.toFixed(2)],
            ['TOTAL', '$' + total.toFixed(2)],
            ['PAYMENTS/CREDITS', '-$' + paymentsCredits.toFixed(2)]
        ];
        
        autoTable(doc, {
            startY: termsHeaderY,
            body: financialData,
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: 2
            },
            columnStyles: {
                0: { cellWidth: termsBoxWidth, fontStyle: 'bold' },
                1: { cellWidth: financialBoxWidth, halign: 'right' }
            },
            didParseCell: function(data) {
                if (data.section === 'body' && data.column.index === 0 && data.row.index === 0) {
                    data.cell.styles.fillColor = [255, 255, 255];
                }
            },
            margin: { left: margin, right: margin }
        });
        
        // Draw terms text box manually
        const termsBoxHeight = ((doc as any).lastAutoTable?.finalY - termsHeaderY) || 20;
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, termsHeaderY, termsBoxWidth, termsBoxHeight);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        const termsLines = doc.splitTextToSize(termsText, termsBoxWidth - 4);
        doc.text(termsLines, margin + 2, termsHeaderY + 4);
        
        yPos = (doc as any).lastAutoTable?.finalY || yPos + 30;
        
        // Final row: Thank you message and Balance Due
        autoTable(doc, {
            startY: yPos,
            body: [
                ['Thank you for your business!', 'BALANCE DUE', '$' + balanceDue.toFixed(2)]
            ],
            theme: 'grid',
            styles: {
                fontSize: 10,
                cellPadding: 3
            },
            columnStyles: {
                0: { 
                    cellWidth: termsBoxWidth,
                    fontStyle: 'italic',
                    halign: 'center'
                },
                1: { 
                    cellWidth: financialBoxWidth * 0.5,
                    fontStyle: 'bold',
                    fillColor: [139, 0, 0],
                    textColor: [255, 255, 255]
                },
                2: { 
                    cellWidth: financialBoxWidth * 0.5,
                    halign: 'right',
                    fontStyle: 'bold',
                    fillColor: [139, 0, 0],
                    textColor: [255, 255, 255]
                }
            },
            margin: { left: margin, right: margin }
        });
        
        // Save/Download PDF
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const invoiceNum = workOrder.InvoiceNumber || workOrder.Id || 'New';
        const filename = `${invoiceNum}_${dateStr}.pdf`;
        doc.save(filename);
    }
}

const singleton = new InvoicePdfService();
export default singleton;
